import type { NotificationSeverity } from '@/store/notifications.store'

export type CreatorNotificationEvent =
  | 'campaign.published'
  | 'campaign.first-claim'
  | 'campaign.cap-reached'
  | 'campaign.ended'
  | 'experience.first-completion'
  | 'experience.session-cap-hit'
  | 'prize.allocated'
  | 'prize.budget-50pct'
  | 'prize.budget-100pct'
  | 'voucher-pool.depleted'
  | 'asset.glb-approved'
  | 'asset.glb-rejected'
  | 'billing.tokens-low'
  | 'billing.tokens-exhausted'

export type CreatorNotificationGroup =
  | 'lifecycle'
  | 'engagement'
  | 'assets'
  | 'billing'

export interface CreatorNotificationPolicy {
  inApp: boolean
  email: boolean
}

export interface CreatorNotificationMeta {
  id: CreatorNotificationEvent
  group: CreatorNotificationGroup
  label: string
  description: string
  titleTemplate: string
  bodyTemplate: string
  actionLabelTemplate?: string
  actionUrlTemplate?: string
  defaultSeverity: NotificationSeverity
  defaultPolicy: CreatorNotificationPolicy
}

const ENTRIES: CreatorNotificationMeta[] = [
  {
    id: 'campaign.published',
    group: 'lifecycle',
    label: 'Campaign published',
    description: 'Fires once when a campaign goes live for the first time.',
    titleTemplate: '"{{campaignName}}" is live',
    bodyTemplate: 'Your campaign just went live and is accepting entries.',
    actionLabelTemplate: 'Open campaign',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'success',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'campaign.first-claim',
    group: 'lifecycle',
    label: 'First claim',
    description: 'Fires the first time anyone successfully claims on this campaign.',
    titleTemplate: 'First claim on "{{campaignName}}"',
    bodyTemplate: 'Someone just completed their first claim. The campaign is officially in the wild.',
    actionLabelTemplate: 'View analytics',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}/analytics',
    defaultSeverity: 'success',
    defaultPolicy: { inApp: true, email: false},
  },
  {
    id: 'campaign.cap-reached',
    group: 'lifecycle',
    label: 'Active-campaign cap hit',
    description: 'Your org has hit its plan limit for simultaneously-active campaigns.',
    titleTemplate: 'Active-campaign cap reached',
    bodyTemplate: 'You\'ve hit the active-campaign limit on your current plan. Pause one or upgrade to launch more.',
    actionLabelTemplate: 'Open billing',
    actionUrlTemplate: '/billing',
    defaultSeverity: 'warning',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'campaign.ended',
    group: 'lifecycle',
    label: 'Campaign ended',
    description: 'Fires when a campaign reaches its scheduled end date.',
    titleTemplate: '"{{campaignName}}" has ended',
    bodyTemplate: 'The campaign finished on schedule. Final analytics are ready to review.',
    actionLabelTemplate: 'View analytics',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}/analytics',
    defaultSeverity: 'info',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'experience.first-completion',
    group: 'engagement',
    label: 'First experience completion',
    description: 'Fires the first time anyone completes the experience (quiz/AR/survey).',
    titleTemplate: 'First completion on "{{campaignName}}"',
    bodyTemplate: 'A player just completed the experience for the first time.',
    actionLabelTemplate: 'View analytics',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}/analytics',
    defaultSeverity: 'success',
    defaultPolicy: { inApp: true, email: false},
  },
  {
    id: 'experience.session-cap-hit',
    group: 'engagement',
    label: 'Per-round session cap hit',
    description: 'A round\'s per-user session cap blocked an attempt.',
    titleTemplate: 'Session cap hit on "{{campaignName}}"',
    bodyTemplate: 'Round {{roundIndex}} hit its per-user session cap. Players are being held out until the next round.',
    actionLabelTemplate: 'Review round config',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'info',
    defaultPolicy: { inApp: true, email: false},
  },
  {
    id: 'prize.allocated',
    group: 'engagement',
    label: 'Prize allocated',
    description: 'Fires every time a prize is allocated to a player.',
    titleTemplate: 'Prize allocated on "{{campaignName}}"',
    bodyTemplate: 'A {{prizeName}} prize was just allocated. {{remainingCount}} remaining in the pool.',
    actionLabelTemplate: 'View prizes',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'info',
    defaultPolicy: { inApp: false, email: false},
  },
  {
    id: 'prize.budget-50pct',
    group: 'engagement',
    label: 'Prize budget 50% used',
    description: 'A round\'s prize-budget bucket has crossed the 50% mark.',
    titleTemplate: '"{{campaignName}}" prize budget half spent',
    bodyTemplate: 'Round {{roundIndex}}\'s prize bucket is 50% allocated. {{remainingCount}} prizes left for this round.',
    actionLabelTemplate: 'Top up prizes',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'info',
    defaultPolicy: { inApp: true, email: false},
  },
  {
    id: 'prize.budget-100pct',
    group: 'engagement',
    label: 'Prize budget exhausted',
    description: 'A round\'s prize-budget bucket is fully allocated; players in that round can no longer win.',
    titleTemplate: '"{{campaignName}}" prize budget exhausted',
    bodyTemplate: 'Round {{roundIndex}}\'s prize bucket is empty. Players will see no-win outcomes until the next round opens.',
    actionLabelTemplate: 'Top up prizes',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'warning',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'voucher-pool.depleted',
    group: 'engagement',
    label: 'Voucher pool depleted',
    description: 'A prize\'s voucher pool has run out of unclaimed codes.',
    titleTemplate: 'Voucher pool empty on "{{campaignName}}"',
    bodyTemplate: 'The {{prizeName}} voucher pool is out of unclaimed codes. Upload more or pause the prize.',
    actionLabelTemplate: 'Manage vouchers',
    actionUrlTemplate: '/dashboard/campaigns/{{campaignId}}',
    defaultSeverity: 'error',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'asset.glb-approved',
    group: 'assets',
    label: 'GLB asset approved',
    description: 'A GLB asset you uploaded has been approved and is ready to use.',
    titleTemplate: 'GLB asset approved: {{fileName}}',
    bodyTemplate: 'Your 3D asset is approved and available in the experience editor.',
    actionLabelTemplate: 'Open editor',
    actionUrlTemplate: '/dashboard/experiences',
    defaultSeverity: 'success',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'asset.glb-rejected',
    group: 'assets',
    label: 'GLB asset rejected',
    description: 'A GLB asset you uploaded was rejected during review.',
    titleTemplate: 'GLB asset rejected: {{fileName}}',
    bodyTemplate: 'Your 3D asset didn\'t pass review. Reason: {{reason}}',
    actionLabelTemplate: 'Re-upload',
    actionUrlTemplate: '/dashboard/experiences',
    defaultSeverity: 'error',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'billing.tokens-low',
    group: 'billing',
    label: 'Tokens low',
    description: 'Your gameplay-token balance has crossed the low-water mark.',
    titleTemplate: 'Gameplay tokens running low',
    bodyTemplate: 'You have {{remainingTokens}} gameplay tokens left across active campaigns. Top up to avoid interruption.',
    actionLabelTemplate: 'Open billing',
    actionUrlTemplate: '/billing',
    defaultSeverity: 'warning',
    defaultPolicy: { inApp: true, email: true},
  },
  {
    id: 'billing.tokens-exhausted',
    group: 'billing',
    label: 'Tokens exhausted',
    description: 'Your gameplay-token balance is zero; live campaigns are now blocked.',
    titleTemplate: 'Gameplay tokens exhausted',
    bodyTemplate: 'You\'re out of gameplay tokens. Active campaigns will redirect players to a coming-back screen until you top up.',
    actionLabelTemplate: 'Top up now',
    actionUrlTemplate: '/billing',
    defaultSeverity: 'error',
    defaultPolicy: { inApp: true, email: true},
  },
]

export const CREATOR_NOTIFICATION_CATALOGUE: ReadonlyArray<CreatorNotificationMeta> = ENTRIES

const BY_ID: ReadonlyMap<CreatorNotificationEvent, CreatorNotificationMeta> = new Map(
  ENTRIES.map(e => [e.id, e])
)

export const CREATOR_NOTIFICATION_GROUPS: ReadonlyArray<{
  id: CreatorNotificationGroup
  label: string
  description: string
}> = [
  { id: 'lifecycle',  label: 'Campaign lifecycle', description: 'Publish, end, cap-hit — the big-picture campaign milestones.' },
  { id: 'engagement', label: 'Engagement & prizes', description: 'First claim, prize-budget thresholds, voucher-pool state.' },
  { id: 'assets',     label: 'Assets',              description: 'GLB and other uploaded-asset review outcomes.' },
  { id: 'billing',    label: 'Billing',             description: 'Token balance, plan limits, top-ups.' },
]

export function getNotificationMeta(
  id: CreatorNotificationEvent
): CreatorNotificationMeta {
  const meta = BY_ID.get(id)
  if (!meta) {
    throw new Error(`Unknown creator notification event: ${id}`)
  }
  return meta
}

export function listNotificationsByGroup(
  group: CreatorNotificationGroup
): CreatorNotificationMeta[] {
  return ENTRIES.filter(e => e.group === group)
}

export function getDefaultPolicy(
  id: CreatorNotificationEvent
): CreatorNotificationPolicy {
  return { ...getNotificationMeta(id).defaultPolicy }
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

export function renderTemplate(
  template: string,
  vars: Record<string, string | number | undefined | null>
): string {
  return template.replace(PLACEHOLDER_RE, (_match, key: string) => {
    const v = vars[key]
    return v === undefined || v === null ? '' : String(v)
  })
}

export interface RenderedNotification {
  title: string
  body: string
  actionLabel?: string
  actionUrl?: string
  severity: NotificationSeverity
}

export function renderNotification(
  id: CreatorNotificationEvent,
  vars: Record<string, string | number | undefined | null>,
  severityOverride?: NotificationSeverity
): RenderedNotification {
  const meta = getNotificationMeta(id)
  return {
    title: renderTemplate(meta.titleTemplate, vars),
    body: renderTemplate(meta.bodyTemplate, vars),
    actionLabel: meta.actionLabelTemplate
      ? renderTemplate(meta.actionLabelTemplate, vars)
      : undefined,
    actionUrl: meta.actionUrlTemplate
      ? renderTemplate(meta.actionUrlTemplate, vars)
      : undefined,
    severity: severityOverride ?? meta.defaultSeverity,
  }
}
